import style from "./Dashboard.module.scss";
import { useState, useEffect } from "react";
// Mui
import LinearProgress from "@mui/material/LinearProgress";
import { DataGrid } from "@mui/x-data-grid";
import Checkbox from "@mui/material/Checkbox";
// API
import useGetAllOrdersApi from "../../API/useGetAllOrdersApi";
import useEditIsShippedApi from "../../API/useEditIsShippedApi";
// Cookies
import { useCookies } from "react-cookie";
//
import { useNavigate } from "react-router-dom";

export default function Dashboard() {
  //
  const navigate = useNavigate();
  // Cookies
  const [cookies, setCookie] = useCookies(["role"]);
  //
  useEffect(() => {
    if (cookies.role === 255) {
      navigate("/dashboard/taxi/dashboard", { replace: true });
    }
    window.scrollTo(0, 0);
  }, []);
  //
  const { data, isPending: isGetAllOrdersPending, isSuccess, fetchStatus } = useGetAllOrdersApi();
  // Format phone number for WhatsApp link
  const formatPhoneForWhatsApp = (phone) => {
    if (!phone) return null;

    // Remove all non-numeric characters
    const cleaned = phone.replace(/\D/g, "");

    // If number starts with '0', replace with country code '966' (Saudi Arabia)
    let formatted = cleaned;
    if (formatted.startsWith("0")) {
      formatted = "966" + formatted.substring(1);
    }

    // If number doesn't have country code, add '966'
    if (formatted.length === 9) {
      formatted = "966" + formatted;
    }

    return formatted;
  };

  // Transform API data to match DataGrid rows
  const [rows, setRows] = useState([]);
  useEffect(() => {
    if (data?.data) {
      const formatted = data.data.map((item, index, array) => ({
        id: item.id,
        index: array.length - index,
        order_number: item.order_number,
        status: item.status,
        date: item.created_at,
        time: item.created_at,
        name: item.client_name,
        client_phone: item.client_phone,
        whatsapp_number: formatPhoneForWhatsApp(item.client_phone),
        payment_method: item.payment_method,
        city: item.city,
        address: item.address,
        subtotal: item.subtotal,
        delivery_fee: item.delivery_fee,
        total_amount: item.total_amount,
        items: JSON.stringify(item.items, null, 2),
        raw_items: item.items,
      }));

      setRows(formatted);
    }
  }, [data]);

  //
  const markAsShippedMutation = useEditIsShippedApi();
  const handleShippedChange = async (id) => {
    // 1) Optimistic UI update
    setRows((prevRows) => prevRows.map((row) => (row.id === id ? { ...row, status: row.status === "shipped" ? "pending" : "shipped" } : row)));

    try {
      // 2) API request
      await markAsShippedMutation.mutateAsync(id);
    } catch (error) {
      console.log(error);

      // 3) Revert if failed
      setRows((prevRows) => prevRows.map((row) => (row.id === id ? { ...row, status: row.status === "shipped" ? "pending" : "shipped" } : row)));
    }
  };

  //
  const columns = [
    {
      field: "status",
      headerName: "Shipped",
      flex: 1,
      minWidth: 75,
      sortable: false,
      headerAlign: "center",
      align: "center",
      disableColumnMenu: true,
      renderCell: (params) => (
        <Checkbox
          checked={params.value === "shipped"}
          onChange={(event) => handleShippedChange(params.row.id, params.value)}
          // disabled={markAsShippedMutation.isPending || fetchStatus === "fetching"}
          sx={{
            color: "#ff9800",
            "&.Mui-checked": {
              color: "#2e7d32", // Green when checked/shipped
            },
            "&.Mui-disabled": {
              color: "#ccc",
            },
            pointerEvents: "auto", // Override the row's pointer-events: none
          }}
        />
      ),
    },

    {
      field: "client_phone",
      headerName: "Phone",
      flex: 1,
      minWidth: 110,
      sortable: false,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        const whatsappNumber = formatPhoneForWhatsApp(params.value);

        if (!whatsappNumber) {
          return <div>{params.value || "N/A"}</div>;
        }

        const whatsappUrl = `https://wa.me/${whatsappNumber}`;

        return (
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: "#25D366",
              textDecoration: "none",
              fontWeight: "bold",
              padding: "4px 8px",
              borderRadius: "4px",
              backgroundColor: "rgba(37, 211, 102, 0.1)",
              "&:hover": {
                backgroundColor: "rgba(37, 211, 102, 0.2)",
              },
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {params.value}
          </a>
        );
      },
    },
    {
      field: "items",
      headerName: "Items",
      flex: 2,
      minWidth: 200,
      sortable: false,
      headerAlign: "center",
      align: "center",
      disableColumnMenu: true,
      renderCell: (params) => (
        <div
          style={{
            // maxHeight: "100px",
            overflow: "auto",
            fontSize: "12px",
            textAlign: "center",
            padding: "4px",

            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            lineHeight: "17px",

            height: "100%", // Take full height of cell
            width: "100%", // Take full width of cell
          }}
        >
          {formatItemsDisplay(params.row.raw_items)}
        </div>
      ),
    },

    {
      field: "index",
      headerName: "#",
      width: 70,
      sortable: false,
      headerAlign: "center",
      align: "center",
      disableColumnMenu: true,
      renderCell: (params) => params.value, // Keep showing the row numbers
    },
    {
      field: "date", // This will be the date part only
      headerName: "Date",
      flex: 1,
      minWidth: 100,
      sortable: false,
      headerAlign: "center",
      align: "center",
      filterable: true,
      renderCell: (params) => {
        return formatDate(params.value);
      },
    },
    {
      field: "time", // This will be the time part only
      headerName: "Time",
      flex: 1,
      minWidth: 100,
      sortable: false,
      headerAlign: "center",
      align: "center",
      filterable: true,
      renderCell: (params) => {
        return formatTime(params.value);
      },
    },
    {
      field: "name",
      headerName: "Name",
      flex: 1,
      minWidth: 175,
      sortable: false,
      headerAlign: "center",
      align: "center",
      filterable: true,
      renderCell: (params) => {
        return params.value ? params.value : <div>N/A</div>;
      },
    },

    // {
    //   field: "city",
    //   headerName: "City",
    //   flex: 1,
    //   minWidth: 100,
    //   sortable: false,
    //   headerAlign: "center",
    //   align: "center",
    // },
    {
      field: "payment_method",
      headerName: "Method",
      flex: 1,
      minWidth: 150,
      sortable: false,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "subtotal",
      headerName: "Subtotal",
      flex: 1,
      minWidth: 100,
      sortable: false,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => `${params.value}`,
    },
    {
      field: "delivery_fee",
      headerName: "Delivery Fee",
      flex: 1,
      minWidth: 100,
      sortable: false,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => `${params.value}`,
    },
    {
      field: "total_amount",
      headerName: "Total",
      flex: 1,
      minWidth: 120,
      sortable: false,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <div
          style={{
            backgroundColor: "#d33030",
            color: "#fff",
            padding: "3px",
          }}
        >
          {params.value}
        </div>
      ),
    },
    {
      field: "address",
      headerName: "Address",
      flex: 1,
      minWidth: 200,
      sortable: false,
      headerAlign: "center",
      align: "center",
    },

    {
      field: "order_number",
      headerName: "Order Number",
      flex: 1,
      minWidth: 300,
      sortable: false,
      headerAlign: "center",
      align: "center",
    },
  ];

  // Format items for display (compact version)
  const formatItemsDisplay = (items) => {
    if (!items || !Array.isArray(items)) return "لا توجد منتجات";

    return (
      <div dir="rtl" style={{ overflow: "auto", fontSize: "12px" }}>
        {items.map((item, index) => (
          <div key={index} style={{ marginBottom: "0px", padding: "0px 0" }}>
            <span style={{ fontWeight: "bold" }}> {item.product_name} </span>
            <br />
            <span style={{ color: "#666", fontSize: "11px" }}>
              ( عدد: {item.quantity} × {item.unit_price} ريال = {item.total_price} ريال )
            </span>
          </div>
        ))}
      </div>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const options = {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    };
    return date.toLocaleDateString("en-GB", options);
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const options = {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    };
    return date.toLocaleTimeString("en-GB", options);
  };

  // Responsive table
  const [containerWidth, setContainerWidth] = useState(window.innerWidth < 600 ? window.innerWidth - 16 : "100%");

  const updateContainerWidth = () => {
    if (window.innerWidth < 600) {
      setContainerWidth(window.innerWidth - 16);
    } else {
      setContainerWidth("100%");
    }
  };

  useEffect(() => {
    // Set initial width
    updateContainerWidth();

    // Update width on window resize
    window.addEventListener("resize", updateContainerWidth);

    // Cleanup event listener on component unmount
    return () => {
      window.removeEventListener("resize", updateContainerWidth);
    };
  }, []);

  return (
    <div className={style.container}>
      {(isGetAllOrdersPending || fetchStatus === "fetching" || markAsShippedMutation.isPending) && (
        <div className={style.progressContainer}>
          <LinearProgress />
        </div>
      )}

      {/*  */}
      <div
        className={style.datagrid_container}
        style={{
          width: containerWidth, // Set width dynamically
        }}
      >
        <DataGrid
          getRowClassName={(params) => (params.row.status === "shipped" ? style.shippedRow : style.NotshippedRow)}
          rows={rows}
          columns={columns}
          getRowHeight={() => "auto"}
          // getRowHeight={() => "auto"} // Makes rows auto-adjust to content
          //  rowHeight={80} // Set fixed height in pixels

          // getRowHeight={({ id, densityFactor }) => {
          //   // You can calculate height based on row data
          //   const row = rows.find((row) => row.id === id);
          //   if (row && row.raw_items && row.raw_items.length > 3) {
          //     return 100; // Taller rows for orders with many items
          //   }
          //   return 60; // Default height
          // }}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 100 },
            },
          }}
          checkboxSelection={false} // Disable checkbox selection
          pageSizeOptions={[10, 25, 50, 100]}
          disableMultipleRowSelection
          // disableColumnFilter // Disable filtering
          disableColumnSort // Disable sorting
          disableMultipleColumnSorting // Disable multiple column sorting
          // disableColumnMenu // Hide column menu
          style={{ width: "100%", height: "100%", overflowX: "auto" }}
          // sx={{
          //   fontFamily: "inherit",
          //   direction: "ltr",
          //   width: "100%",
          //   height: "100%",
          //   overflowX: "auto",
          //   "& .MuiDataGrid-footerContainer p": {
          //     margin: 0,
          //   },
          // }}
          sx={{
            "& .MuiDataGrid-cell": {
              whiteSpace: "normal",
              lineHeight: "1.4",
              py: 1, // padding top/bottom so it doesn't look cramped
              display: "flex",
              alignItems: "center",
            },
          }}
          disableRowSelectionOnClick // Prevents selection on click
          disableVirtualization // Sometimes helps with styling issues
        />
      </div>
    </div>
  );
}
